import { QueryEventsRequest } from "@garther/interfaces";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { eventsApiClient } from "@/libs/api";

// Query keys for consistent cache management
export const eventsKeys = {
  all: ["events"] as const,
  lists: () => [...eventsKeys.all, "list"] as const,
  list: (filters: QueryEventsRequest) => [...eventsKeys.lists(), filters] as const,
  details: () => [...eventsKeys.all, "detail"] as const,
  detail: (id: string) => [...eventsKeys.details(), id] as const
};

interface UseEventsOptions {
  limit?: number;
  hostId?: string;
  categoryId?: string[];
  page?: number;
  enabled?: boolean;
}

export function useEvents(options: UseEventsOptions = {}) {
  const { limit = 6, categoryId, hostId, page = 1, enabled = true } = options;

  return useQuery({
    queryKey: eventsKeys.list({ limit, categoryId, hostId, page }),
    queryFn: async () => {
      const queryOptions: QueryEventsRequest = { limit, categoryId, hostId, page };
      return eventsApiClient.getAvailableEvents(queryOptions);
    },
    enabled,
    staleTime: 5 * 60 * 1000 // 5 minutes for events
  });
}

export function useEvent(eventId: string, enabled = true) {
  return useQuery({
    queryKey: eventsKeys.detail(eventId),
    queryFn: () => eventsApiClient.getEventById(eventId),
    enabled: enabled && !!eventId,
    staleTime: 10 * 60 * 1000 // 10 minutes for individual events
  });
}

// Pagination-specific hook with state management
export function useEventsPagination(options: UseEventsOptions = {}) {
  const { enabled = true } = options;
  const [currentPage, setCurrentPage] = useState(1);

  const query = useEvents({
    ...options,
    page: currentPage,
    enabled
  });

  const setPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return {
    ...query,
    currentPage,
    setPage,
    pagination: query.data?.meta || null,
    events: query.data?.data || []
  };
}

// Utility hook for invalidating events cache
export function useEventsInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: eventsKeys.all }),
    invalidateList: () => queryClient.invalidateQueries({ queryKey: eventsKeys.lists() }),
    invalidateEvent: (eventId: string) =>
      queryClient.invalidateQueries({ queryKey: eventsKeys.detail(eventId) })
  };
}
